/**
 * PAY-4631: the ten construction celebrations, and the registry of all
 * twenty (PAY-4644 adds the second ten in `construction-scene-second-ten.ts`).
 *
 * Each style is built once from a seed and the stage (viewport plus the
 * card's resting rectangle) into a {@link ConstructionScene}: a `draw` for the
 * canvas and a `pose` for the card. Six of them carry the card in (crane,
 * dump truck, concrete pour, excavator, forklift, the brick capstone); the
 * rest hold it back until a moment in the timeline. Everything is a function
 * of time, so the canvas and the card are always on the same frame and a test
 * can sample any moment. Every style ends with the card at rest.
 */

import {
  CELEBRATION_DURATION_S,
  type CelebrationStyle,
  type Rng,
} from './celebration-variety';
import {
  BOUNCE_CONTACT,
  type Builder,
  type CardPlacement,
  type ConstructionScene,
  HIDDEN,
  BRICKS,
  type Chunk,
  CONCRETE,
  CONCRETE_GREY,
  DIRT,
  GLASS,
  ORANGE,
  type Point,
  STEEL,
  STEEL_LIGHT,
  type StageGeometry,
  TAU,
  type Thrown,
  TYRE,
  YELLOW,
  YELLOW_DARK,
  cardPerimeter,
  circle,
  clamp,
  drawBrick,
  drawChunks,
  drawCone,
  drawPuffs,
  drawWheel,
  easeBack,
  easeBounce,
  easeIn,
  easeInOut,
  easeOut,
  lerp,
  makePuffs,
  pick,
  propsAlpha,
  rand,
  roundRect,
  seg,
  thrownAt,
} from './construction-kit';
import { SECOND_TEN } from './construction-scene-second-ten';

export type { ConstructionScene } from './construction-kit';

/** Where the card is, as an offset from its resting place. */
export interface CardPose {
  dx: number;
  dy: number;
  rotation: number;
  scale: number;
  opacity: number;
}

export const RESTING_POSE: CardPose = {
  dx: 0,
  dy: 0,
  rotation: 0,
  scale: 1,
  opacity: 1,
};

// ── 1. Brick by brick ──────────────────────────────────────────────────────
const bricks: Builder = (stage, rng) => {
  // Bricks grow with the screen, so a 4K wall is still a few hundred bricks.
  const size = Math.max(1, stage.width / 1600);
  const bw = 46 * size;
  const bh = 20 * size;
  const gap = 4 * size;
  const rowH = bh + gap;
  // The wall's top course sits just under the card's resting bottom edge, so
  // the card lands on it.
  const rows = Math.max(
    3,
    Math.floor((stage.height - (stage.bottom + 4)) / rowH),
  );
  const pieces: {
    x: number;
    y: number;
    color: string;
    at: number;
    fall: number;
    spin: number;
    drift: number;
  }[] = [];
  for (let row = 0; row < rows; row += 1) {
    const offset = row % 2 ? -(bw + gap) / 2 : 0;
    for (let x = offset; x < stage.width; x += bw + gap) {
      pieces.push({
        x,
        y: stage.height - (row + 1) * rowH + gap / 2,
        color: pick(rng, BRICKS),
        at:
          0.1 +
          row * (2.5 / rows) +
          rand(rng, 0, 0.2) +
          (Math.abs(x + bw / 2 - stage.width / 2) / stage.width) * 0.4,
        fall: 5.3 + (rows - row) * 0.05 + rand(rng, 0, 0.35),
        spin: rand(rng, -3, 3),
        drift: rand(rng, -60, 60),
      });
    }
  }
  const drop = { from: 3.2, to: 4.0 };
  const puffs = makePuffs(
    rng,
    16,
    stage.left,
    stage.right,
    stage.bottom,
    drop.from + (drop.to - drop.from) * BOUNCE_CONTACT,
  );

  return {
    settledAt: drop.to,
    place: (t) =>
      t < drop.from
        ? HIDDEN
        : {
            y: lerp(
              -stage.cardHeight,
              stage.cy,
              easeBounce(seg(t, drop.from, drop.to)),
            ),
          },
    draw(ctx, t) {
      for (const brick of pieces) {
        if (t < brick.at) continue;
        const p = seg(t, brick.at, brick.at + 0.42);
        let y = lerp(brick.y - 160 * size, brick.y, easeBounce(p));
        let x = brick.x;
        let rotation = 0;
        let alpha = Math.min(1, p * 4);
        if (t > brick.fall) {
          const k = t - brick.fall;
          y += 750 * k * k;
          x += brick.drift * k;
          rotation = brick.spin * k;
          alpha *= 1 - seg(t, brick.fall + 0.4, brick.fall + 1);
        }
        if (alpha <= 0) continue;
        ctx.globalAlpha = alpha;
        drawBrick(ctx, x, y, bw, bh, brick.color, rotation);
      }
      ctx.globalAlpha = 1;
      drawPuffs(ctx, puffs, t);
    },
  };
};

// ── 2. Tower crane ─────────────────────────────────────────────────────────
const crane: Builder = (stage) => {
  const towerX = Math.max(70, stage.width * 0.09);
  const jibY = Math.max(34, stage.height * 0.07);
  const parked = towerX + 110;
  const sling = 70 + stage.cardHeight / 2;

  const trolleyX = (t: number) =>
    t < 4.6
      ? lerp(parked, stage.cx, easeInOut(seg(t, 0.4, 1.6)))
      : lerp(stage.cx, parked, easeInOut(seg(t, 5.0, 6.4)));
  // The hook drops off the bottom of the screen, picks the card up there and
  // hoists it to rest.
  const hookY = (t: number) => {
    const up = jibY + 40;
    const floor = stage.height + 30;
    const low = stage.top - 70;
    if (t < 2.6) return lerp(up, floor, easeInOut(seg(t, 1.2, 2.4)));
    if (t < 4.6) return lerp(floor, low, easeInOut(seg(t, 2.6, 4.2)));
    return lerp(low, up, easeInOut(seg(t, 4.6, 5.6)));
  };
  const swing = (t: number) =>
    t < 2.6
      ? 0
      : 0.06 *
        Math.sin(4 * (t - 2.6)) *
        Math.exp(-1.1 * (t - 2.6)) *
        (1 - seg(t, 4.0, 4.4));
  const place = (t: number): CardPlacement => {
    if (t >= 4.4) return {};
    const rotation = swing(t);
    return {
      x: trolleyX(t) - Math.sin(rotation) * sling,
      y: hookY(t) + Math.cos(rotation) * sling,
      rotation,
      opacity: t < 2.4 ? 0 : 1,
    };
  };

  return {
    settledAt: 4.4,
    place,
    draw(ctx, t) {
      const { width, height } = stage;
      ctx.globalAlpha = propsAlpha(t);
      // Tower.
      ctx.strokeStyle = YELLOW;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(towerX - 12, height);
      ctx.lineTo(towerX - 12, jibY + 16);
      ctx.moveTo(towerX + 12, height);
      ctx.lineTo(towerX + 12, jibY + 16);
      ctx.stroke();
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = YELLOW_DARK;
      ctx.beginPath();
      for (let y = height, i = 0; y > jibY + 16; y -= 26, i += 1) {
        ctx.moveTo(i % 2 ? towerX + 12 : towerX - 12, y);
        ctx.lineTo(
          i % 2 ? towerX - 12 : towerX + 12,
          Math.max(jibY + 16, y - 26),
        );
      }
      ctx.stroke();
      // Jib.
      ctx.strokeStyle = YELLOW;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(towerX - 130, jibY);
      ctx.lineTo(width - 30, jibY);
      ctx.moveTo(towerX - 130, jibY + 16);
      ctx.lineTo(width - 30, jibY + 16);
      ctx.stroke();
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = YELLOW_DARK;
      ctx.beginPath();
      for (let x = towerX - 130, i = 0; x < width - 30; x += 22, i += 1) {
        ctx.moveTo(x, i % 2 ? jibY : jibY + 16);
        ctx.lineTo(Math.min(width - 30, x + 22), i % 2 ? jibY + 16 : jibY);
      }
      ctx.stroke();
      // Apex and pendant lines.
      ctx.strokeStyle = YELLOW;
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(towerX - 12, jibY);
      ctx.lineTo(towerX, jibY - 56);
      ctx.lineTo(towerX + 12, jibY);
      ctx.stroke();
      ctx.strokeStyle = STEEL_LIGHT;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(towerX, jibY - 56);
      ctx.lineTo(towerX - 130, jibY);
      ctx.moveTo(towerX, jibY - 56);
      ctx.lineTo(width * 0.62, jibY);
      ctx.stroke();
      // Counterweight and cab.
      ctx.fillStyle = STEEL;
      roundRect(ctx, towerX - 128, jibY + 16, 50, 34, 3);
      ctx.fill();
      ctx.fillStyle = YELLOW;
      roundRect(ctx, towerX + 14, jibY + 16, 36, 28, 4);
      ctx.fill();
      ctx.fillStyle = GLASS;
      roundRect(ctx, towerX + 30, jibY + 21, 16, 13, 2);
      ctx.fill();
      // Trolley, cable and hook.
      const tx = trolleyX(t);
      const hy = hookY(t);
      ctx.fillStyle = STEEL;
      roundRect(ctx, tx - 16, jibY + 16, 32, 8, 2);
      ctx.fill();
      ctx.strokeStyle = STEEL;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(tx - 4, jibY + 24);
      ctx.lineTo(tx - 4, hy - 10);
      ctx.moveTo(tx + 4, jibY + 24);
      ctx.lineTo(tx + 4, hy - 10);
      ctx.stroke();
      ctx.fillStyle = YELLOW;
      roundRect(ctx, tx - 11, hy - 12, 22, 20, 4);
      ctx.fill();
      ctx.strokeStyle = STEEL;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(tx, hy + 16, 7, -Math.PI / 2, Math.PI * 0.9);
      ctx.stroke();
      // Slings to the card's top corners while it hangs.
      if (t > 2.4 && t < 4.5) {
        const card = place(t);
        const r = card.rotation ?? 0;
        const cardX = card.x ?? stage.cx;
        const cardY = card.y ?? stage.cy;
        const corner = (sx: number) => ({
          x: cardX + Math.cos(r) * sx + Math.sin(r) * (stage.cardHeight / 2),
          y: cardY + Math.sin(r) * sx - Math.cos(r) * (stage.cardHeight / 2),
        });
        const a = corner(-stage.cardWidth * 0.38);
        const b = corner(stage.cardWidth * 0.38);
        ctx.globalAlpha *= 1 - seg(t, 4.3, 4.5);
        ctx.strokeStyle = STEEL;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(tx, hy + 20);
        ctx.lineTo(a.x, a.y);
        ctx.moveTo(tx, hy + 20);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    },
  };
};

// ── 3. Dump truck ──────────────────────────────────────────────────────────
interface Sheet extends Thrown {
  spin: number;
  flutter: number;
  sway: number;
  phase: number;
  color: string | null;
}

const dumpTruck: Builder = (stage, rng, palette) => {
  const ground = Math.max(130, Math.min(stage.top - 10, 170));
  const stop = stage.cx - 70;
  const hingeX = stop - 6;
  const hingeY = ground - 46;
  const sheets: Sheet[] = Array.from({ length: 70 }, () => ({
    x: hingeX + rand(rng, -8, 8),
    y: hingeY + rand(rng, -10, 10),
    vx: rand(rng, -330, 60),
    vy: rand(rng, -170, 40),
    g: 380,
    te: rand(rng, 2.4, 3.6),
    life: rand(rng, 2.6, 3.6),
    spin: rand(rng, -5, 5),
    flutter: rand(rng, 3, 6),
    sway: rand(rng, 10, 34),
    phase: rand(rng, 0, TAU),
    color: rng() < 0.45 ? pick(rng, palette) : null,
  }));
  const dirt: Chunk[] = Array.from({ length: 46 }, () => ({
    x: hingeX + rand(rng, -6, 6),
    y: hingeY,
    vx: rand(rng, -220, 30),
    vy: rand(rng, -120, 60),
    g: 1200,
    te: rand(rng, 2.25, 3.1),
    life: 1.4,
    size: rand(rng, 3, 7),
    spin: rand(rng, -6, 6),
    color: pick(rng, DIRT),
  }));
  const load = Array.from({ length: 26 }, () => ({
    x: rand(rng, 4, 140),
    y: -62 - rand(rng, 0, 16) * Math.sin(rand(rng, 0, Math.PI)),
    rotation: rand(rng, -0.6, 0.6),
    color: rng() < 0.45 ? pick(rng, palette) : '#FFFFFF',
  }));

  const truckX = (t: number) =>
    t < 1.8
      ? lerp(-320, stop, easeOut(seg(t, 0, 1.8)))
      : lerp(stop, stage.width + 80, easeIn(seg(t, 4.8, 6.4)));
  const tilt = (t: number) =>
    0.78 * (easeOut(seg(t, 1.9, 2.8)) - easeInOut(seg(t, 3.8, 4.6)));

  return {
    settledAt: 3.6,
    place(t) {
      if (t < 2.5) return HIDDEN;
      const p = seg(t, 2.5, 3.6);
      return {
        x: lerp(stop - 30, stage.cx, easeOut(p)),
        y: lerp(ground - 40, stage.cy, easeBounce(p)),
        scale: lerp(0.2, 1, easeOut(p)),
        rotation: lerp(-0.9, 0, easeOut(p)),
        opacity: seg(t, 2.5, 2.62),
      };
    },
    draw(ctx, t) {
      const hx = truckX(t);
      const angle = tilt(t);
      const remaining = Math.round(load.length * (1 - seg(t, 2.3, 3.4)));
      drawChunks(ctx, dirt, t);
      for (const sheet of sheets) {
        const at = thrownAt(sheet, t);
        if (!at) continue;
        ctx.save();
        ctx.globalAlpha = 1 - seg(at.k, 0.75, 1);
        ctx.translate(
          at.x + Math.sin(at.age * sheet.flutter + sheet.phase) * sheet.sway,
          at.y,
        );
        ctx.rotate(
          sheet.spin * at.age + Math.sin(at.age * sheet.flutter) * 0.5,
        );
        ctx.fillStyle = sheet.color ?? '#FFFFFF';
        ctx.strokeStyle = 'rgba(15,23,42,.25)';
        ctx.lineWidth = 1;
        roundRect(ctx, -8, -10.5, 16, 21, 2);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = 'rgba(15,23,42,.3)';
        for (const line of [-5, -1, 3]) ctx.fillRect(-5, line, 10, 1.5);
        ctx.restore();
      }
      ctx.globalAlpha = 1;
      // Chassis and cab (the truck faces right).
      ctx.fillStyle = STEEL;
      ctx.fillRect(hx - 14, ground - 40, 252, 12);
      ctx.fillStyle = YELLOW;
      roundRect(ctx, hx + 160, ground - 96, 76, 60, 8);
      ctx.fill();
      ctx.fillStyle = GLASS;
      roundRect(ctx, hx + 196, ground - 88, 32, 24, 4);
      ctx.fill();
      ctx.fillStyle = YELLOW_DARK;
      ctx.fillRect(hx + 160, ground - 46, 76, 8);
      // Ram from the chassis to the underside of the raised bed.
      if (angle > 0.02) {
        const ramX = hx + Math.cos(-angle) * 110 + Math.sin(-angle) * 8;
        const ramY =
          ground - 40 + Math.sin(-angle) * 110 - Math.cos(-angle) * 8;
        ctx.strokeStyle = STEEL_LIGHT;
        ctx.lineWidth = 6;
        ctx.beginPath();
        ctx.moveTo(hx + 128, ground - 34);
        ctx.lineTo(ramX, ramY);
        ctx.stroke();
      }
      // The bed pivots on its rear hinge.
      ctx.save();
      ctx.translate(hx, ground - 40);
      ctx.rotate(-angle);
      for (let i = 0; i < remaining; i += 1) {
        const piece = load[i]!;
        ctx.save();
        ctx.translate(piece.x, piece.y);
        ctx.rotate(piece.rotation);
        ctx.fillStyle = piece.color;
        ctx.fillRect(-8, -6, 16, 12);
        ctx.restore();
      }
      ctx.fillStyle = ORANGE;
      ctx.beginPath();
      ctx.moveTo(-6, 0);
      ctx.lineTo(150, 0);
      ctx.lineTo(158, -62);
      ctx.lineTo(-14, -62);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,.18)';
      ctx.lineWidth = 3;
      ctx.beginPath();
      for (const rib of [28, 68, 108]) {
        ctx.moveTo(rib, -6);
        ctx.lineTo(rib + 2, -56);
      }
      ctx.stroke();
      ctx.restore();
      for (const wheel of [18, 64, 206]) {
        drawWheel(ctx, hx + wheel, ground - 18, 18, hx / 18);
      }
    },
  };
};

// ── 4. Concrete pour ───────────────────────────────────────────────────────
const concretePour: Builder = (stage, rng) => {
  const ground = stage.height - 14;
  // The truck faces left. Its chute hinges at fx + 268 and, swung out, ends
  // about 60px to the right; the pour lands 40px past that, under the card.
  const stop = stage.cx - 369;
  const splash: Chunk[] = Array.from({ length: 70 }, () => ({
    x: stage.cx + rand(rng, -16, 16),
    y: ground - 6,
    vx: rand(rng, -160, 160),
    vy: rand(rng, -260, -80),
    g: 1000,
    te: rand(rng, 2.5, 3.9),
    life: 0.8,
    size: rand(rng, 3, 6),
    spin: 0,
    color: pick(rng, CONCRETE),
  }));
  const drips: (Thrown & { size: number })[] = Array.from(
    { length: 26 },
    () => ({
      x: rand(rng, stage.left + 18, stage.right - 18),
      y: stage.bottom - 2,
      vx: 0,
      vy: rand(rng, 0, 30),
      g: 900,
      te: rand(rng, 4.1, 5.4),
      life: 0.9,
      size: rand(rng, 3, 6),
    }),
  );

  const frontX = (t: number) =>
    t < 2
      ? lerp(stage.width + 40, stop, easeOut(seg(t, 0, 2)))
      : lerp(stop, -420, easeIn(seg(t, 4.8, 6.4)));
  // Folded upright while driving, swung out to pour.
  const chute = (t: number) => {
    const hinge = { x: frontX(t) + 268, y: ground - 80 };
    const angle = lerp(
      -Math.PI / 2 - 0.35,
      0.55,
      easeInOut(seg(t, 2.0, 2.4)) - easeInOut(seg(t, 4.2, 4.6)),
    );
    return {
      hinge,
      end: {
        x: hinge.x + Math.cos(angle) * 72,
        y: hinge.y + Math.sin(angle) * 72,
      },
    };
  };

  return {
    settledAt: 4.2,
    place(t) {
      if (t < 3.0) return HIDDEN;
      const p = seg(t, 3.0, 4.2);
      return {
        y: lerp(stage.height + stage.cardHeight / 2, stage.cy, easeBack(p)),
        scale: lerp(0.8, 1, p),
        opacity: seg(t, 3.0, 3.3),
      };
    },
    draw(ctx, t) {
      const fx = frontX(t);
      const { hinge, end } = chute(t);
      const spread = seg(t, 2.5, 4.0);
      const slabAlpha = 1 - seg(t, 5.8, 7.2);
      if (spread > 0 && slabAlpha > 0) {
        ctx.globalAlpha = slabAlpha;
        ctx.fillStyle = CONCRETE_GREY;
        ctx.beginPath();
        ctx.ellipse(
          stage.cx,
          ground + 4,
          30 + 240 * spread,
          6 + 22 * spread,
          0,
          Math.PI,
          TAU,
        );
        ctx.fill();
        ctx.fillStyle = 'rgba(255,255,255,.25)';
        ctx.beginPath();
        ctx.ellipse(
          stage.cx - 30 * spread,
          ground - 6 * spread,
          110 * spread,
          4 * spread + 1,
          0,
          Math.PI,
          TAU,
        );
        ctx.fill();
        ctx.globalAlpha = 1;
      }
      const flow = seg(t, 2.4, 2.55) * (1 - seg(t, 3.7, 3.95));
      if (flow > 0) {
        ctx.strokeStyle = CONCRETE_GREY;
        ctx.lineWidth = 13 * flow;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(end.x, end.y);
        ctx.quadraticCurveTo(
          end.x + 22 + Math.sin(t * 22) * 2,
          end.y + 2,
          stage.cx,
          ground - 2,
        );
        ctx.stroke();
      }
      drawChunks(ctx, splash, t);
      ctx.fillStyle = CONCRETE_GREY;
      for (const drip of drips) {
        const at = thrownAt(drip, t);
        if (!at) continue;
        ctx.globalAlpha = 1 - at.k;
        ctx.beginPath();
        ctx.ellipse(at.x, at.y, drip.size * 0.6, drip.size, 0, 0, TAU);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      // Chassis, drum stands and cab. Every part is placed from fx.
      ctx.fillStyle = STEEL;
      ctx.fillRect(fx + 10, ground - 44, 280, 12);
      for (const stand of [96, 250]) {
        ctx.beginPath();
        ctx.moveTo(fx + stand - 14, ground - 44);
        ctx.lineTo(fx + stand, ground - 74);
        ctx.lineTo(fx + stand + 14, ground - 44);
        ctx.fill();
      }
      ctx.fillStyle = YELLOW;
      roundRect(ctx, fx, ground - 104, 76, 64, 8);
      ctx.fill();
      ctx.fillStyle = GLASS;
      roundRect(ctx, fx + 8, ground - 96, 32, 26, 4);
      ctx.fill();
      // The drum, its stripes turning.
      ctx.save();
      ctx.translate(fx + 178, ground - 90);
      ctx.rotate(-0.14);
      const drum = () => {
        ctx.beginPath();
        ctx.ellipse(0, 0, 94, 42, 0, 0, TAU);
      };
      drum();
      ctx.fillStyle = '#E2E8F0';
      ctx.fill();
      ctx.save();
      drum();
      ctx.clip();
      ctx.fillStyle = ORANGE;
      const turn = t * 120;
      for (let k = 0; k < 7; k += 1) {
        const x = ((((k * 46 + turn) % 322) + 322) % 322) - 160;
        ctx.beginPath();
        ctx.moveTo(x, -50);
        ctx.lineTo(x + 18, -50);
        ctx.lineTo(x + 48, 50);
        ctx.lineTo(x + 30, 50);
        ctx.closePath();
        ctx.fill();
      }
      ctx.restore();
      drum();
      ctx.strokeStyle = STEEL;
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.fillStyle = STEEL;
      roundRect(ctx, 86, -12, 14, 24, 4);
      ctx.fill();
      ctx.restore();
      ctx.strokeStyle = YELLOW_DARK;
      ctx.lineWidth = 10;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(hinge.x, hinge.y);
      ctx.lineTo(end.x, end.y);
      ctx.stroke();
      ctx.fillStyle = STEEL;
      circle(ctx, hinge.x, hinge.y, 5);
      ctx.fill();
      for (const wheel of [34, 206, 252]) {
        drawWheel(ctx, fx + wheel, ground - 18, 18, -fx / 18);
      }
    },
  };
};

// ── 5. Excavator ───────────────────────────────────────────────────────────
const excavator: Builder = (stage, rng) => {
  const { height } = stage;
  const baseX = Math.max(16, stage.width * 0.04);
  const pivotAt = (bx: number) => ({ x: bx + 150, y: height - 74 });
  const restingPivot = pivotAt(baseX);
  const dig = { x: stage.cx, y: height + 4 };
  // Bucket rim one pixel under the card's resting bottom edge.
  const lift = { x: stage.cx, y: stage.bottom + 2 };
  const link = Math.max(
    150,
    Math.hypot(lift.x - restingPivot.x, lift.y - restingPivot.y) * 0.56,
    Math.hypot(dig.x - restingPivot.x, dig.y - restingPivot.y) * 0.56,
  );

  const bodyX = (t: number) =>
    t < 1.0
      ? lerp(-380, baseX, easeOut(seg(t, 0, 1.0)))
      : baseX - easeIn(seg(t, 5.4, 7.0)) * (baseX + 460);
  const target = (t: number): Point => {
    const rest = { x: bodyX(t) + 150 + link * 1.15, y: height - 26 };
    const mix = (a: Point, b: Point, p: number) => ({
      x: lerp(a.x, b.x, p),
      y: lerp(a.y, b.y, p),
    });
    if (t < 1.0) return rest;
    if (t < 1.8) return mix(rest, dig, easeInOut(seg(t, 1.0, 1.8)));
    if (t < 2.2) return dig;
    if (t < 3.6) return mix(dig, lift, easeInOut(seg(t, 2.2, 3.6)));
    if (t < 4.0) return lift;
    return mix(lift, rest, easeInOut(seg(t, 4.0, 5.2)));
  };
  // Two-link IK from the boom pivot. The bucket, the card and the falling
  // dirt all hang off the solved tip, so the parts cannot drift apart.
  const arm = (t: number) => {
    const bx = bodyX(t);
    const pivot = pivotAt(bx);
    const aim = target(t);
    const dx = aim.x - pivot.x;
    const dy = aim.y - pivot.y;
    const reach = Math.hypot(dx, dy) || 1;
    const d = clamp(reach, 1, 2 * link * 0.999);
    const base = Math.atan2(dy, dx);
    const bend = Math.acos(d / (2 * link));
    const up = {
      x: pivot.x + Math.cos(base - bend) * link,
      y: pivot.y + Math.sin(base - bend) * link,
    };
    const down = {
      x: pivot.x + Math.cos(base + bend) * link,
      y: pivot.y + Math.sin(base + bend) * link,
    };
    return {
      bx,
      pivot,
      elbow: up.y < down.y ? up : down,
      tip: { x: pivot.x + (dx / reach) * d, y: pivot.y + (dy / reach) * d },
    };
  };
  // 0 is level with the opening up (carrying); positive tips the teeth down.
  const bucketAngle = (t: number) =>
    lerp(
      lerp(0.9, 0, easeInOut(seg(t, 1.8, 2.2))),
      1.1,
      easeInOut(seg(t, 3.7, 4.1)),
    );

  const spray: Chunk[] = Array.from({ length: 60 }, () => ({
    x: dig.x + rand(rng, -20, 20),
    y: height,
    vx: rand(rng, -260, 260),
    vy: rand(rng, -700, -300),
    g: 1400,
    te: rand(rng, 1.75, 2.2),
    life: 1.2,
    size: rand(rng, 4, 9),
    spin: rand(rng, -8, 8),
    color: pick(rng, DIRT),
  }));
  const trickle: Chunk[] = Array.from({ length: 30 }, () => {
    const te = rand(rng, 2.3, 3.4);
    const from = arm(te).tip;
    return {
      x: from.x + rand(rng, -18, 18),
      y: from.y + 20,
      vx: rand(rng, -30, 30),
      vy: 0,
      g: 1200,
      te,
      life: 1,
      size: rand(rng, 3, 6),
      spin: rand(rng, -6, 6),
      color: pick(rng, DIRT),
    };
  });

  return {
    settledAt: 3.6,
    place(t) {
      if (t < 2.1) return HIDDEN;
      if (t >= 3.6) return {};
      const { tip } = arm(t);
      const scale = lerp(0.3, 1, easeInOut(seg(t, 2.2, 3.6)));
      return {
        x: tip.x,
        y: tip.y - 2 - (stage.cardHeight * scale) / 2,
        scale,
        opacity: seg(t, 2.1, 2.4),
      };
    },
    draw(ctx, t) {
      const { bx, pivot, elbow, tip } = arm(t);
      const mound = seg(t, 1.8, 2.1) * (1 - seg(t, 5.0, 6.0));
      if (mound > 0) {
        ctx.globalAlpha = mound;
        ctx.fillStyle = DIRT[0]!;
        ctx.beginPath();
        ctx.ellipse(dig.x, height, 90, 24, 0, Math.PI, TAU);
        ctx.fill();
        ctx.globalAlpha = 1;
      }
      drawChunks(ctx, spray, t);
      drawChunks(ctx, trickle, t);
      // Tracks and body.
      ctx.fillStyle = TYRE;
      roundRect(ctx, bx, height - 34, 190, 28, 14);
      ctx.fill();
      ctx.fillStyle = STEEL_LIGHT;
      for (let i = 0; i < 5; i += 1) {
        circle(ctx, bx + 22 + i * 36, height - 20, 7);
        ctx.fill();
      }
      ctx.fillStyle = STEEL;
      roundRect(ctx, bx - 6, height - 84, 34, 42, 4);
      ctx.fill();
      ctx.fillStyle = YELLOW;
      roundRect(ctx, bx + 10, height - 92, 160, 54, 6);
      ctx.fill();
      roundRect(ctx, bx + 24, height - 146, 72, 58, 6);
      ctx.fill();
      ctx.fillStyle = GLASS;
      roundRect(ctx, bx + 52, height - 138, 36, 30, 4);
      ctx.fill();
      ctx.fillStyle = STEEL;
      ctx.fillRect(bx + 120, height - 112, 6, 22);
      // Arm.
      ctx.lineCap = 'round';
      ctx.strokeStyle = STEEL_LIGHT;
      ctx.lineWidth = 6;
      ctx.beginPath();
      ctx.moveTo(pivot.x - 30, pivot.y + 8);
      ctx.lineTo(lerp(pivot.x, elbow.x, 0.6), lerp(pivot.y, elbow.y, 0.6));
      ctx.stroke();
      ctx.strokeStyle = YELLOW;
      ctx.lineWidth = 16;
      ctx.beginPath();
      ctx.moveTo(pivot.x, pivot.y);
      ctx.lineTo(elbow.x, elbow.y);
      ctx.stroke();
      ctx.strokeStyle = YELLOW_DARK;
      ctx.lineWidth = 11;
      ctx.beginPath();
      ctx.moveTo(elbow.x, elbow.y);
      ctx.lineTo(tip.x, tip.y);
      ctx.stroke();
      // Bucket.
      ctx.save();
      ctx.translate(tip.x, tip.y);
      ctx.rotate(bucketAngle(t));
      ctx.fillStyle = STEEL;
      ctx.beginPath();
      ctx.moveTo(-28, 0);
      ctx.lineTo(28, 0);
      ctx.quadraticCurveTo(30, 30, 0, 30);
      ctx.quadraticCurveTo(-30, 30, -28, 0);
      ctx.fill();
      ctx.fillStyle = STEEL_LIGHT;
      for (let i = 0; i < 4; i += 1) {
        ctx.beginPath();
        ctx.moveTo(28, -2 + i * 8);
        ctx.lineTo(38, 2 + i * 8);
        ctx.lineTo(28, 6 + i * 8);
        ctx.fill();
      }
      ctx.restore();
      ctx.fillStyle = STEEL;
      for (const joint of [pivot, elbow, tip]) {
        circle(ctx, joint.x, joint.y, 6);
        ctx.fill();
      }
    },
  };
};

// ── 6. Wrecking ball ───────────────────────────────────────────────────────
const IMPACT_AT = 1.4;

const wreckingBall: Builder = (stage, rng) => {
  const bw = 44;
  const bh = 20;
  const gap = 4;
  const wallLeft = stage.left - 28;
  const wallTop = stage.top - 28;
  const wallWidth = stage.cardWidth + 56;
  const wallHeight = stage.cardHeight + 56;
  // The ball hits the wall's left edge at the bottom of its swing.
  const impact = { x: wallLeft, y: stage.cy };
  const radius = 36;
  const pivot = { x: wallLeft - radius, y: -Math.max(260, stage.height * 0.4) };
  const chain = stage.cy - pivot.y;
  const rows = Math.ceil(wallHeight / (bh + gap));

  const wall: {
    x: number;
    y: number;
    w: number;
    color: string;
    at: number;
    vx: number;
    vy: number;
    spin: number;
  }[] = [];
  for (let row = 0; row < rows; row += 1) {
    const offset = row % 2 ? -(bw + gap) / 2 : 0;
    for (let x = wallLeft + offset; x < wallLeft + wallWidth; x += bw + gap) {
      const x0 = Math.max(wallLeft, x);
      const x1 = Math.min(wallLeft + wallWidth, x + bw);
      if (x1 - x0 < 8) continue;
      const y = wallTop + wallHeight - (row + 1) * (bh + gap);
      const dx = (x0 + x1) / 2 - impact.x;
      const dy = y + bh / 2 - impact.y;
      const distance = Math.hypot(dx, dy) || 1;
      const speed =
        rand(rng, 400, 900) * (0.55 + 0.45 * (1 - distance / (wallWidth + 1)));
      wall.push({
        x: x0,
        y,
        w: x1 - x0,
        color: pick(rng, BRICKS),
        at: row * (0.75 / rows),
        vx: (dx / distance) * speed + 260,
        vy: (dy / distance) * speed - rand(rng, 100, 350),
        spin: rand(rng, -8, 8),
      });
    }
  }
  const puffs = makePuffs(
    rng,
    22,
    wallLeft - 10,
    wallLeft + wallWidth * 0.6,
    stage.cy,
    IMPACT_AT,
    2.4,
  );
  const swing = (t: number) =>
    t < IMPACT_AT
      ? -1.15 * Math.cos((Math.PI / 2) * seg(t, 0, IMPACT_AT))
      : 0.55 *
        Math.sin((Math.PI / 1.4) * (t - IMPACT_AT)) *
        Math.exp(-0.35 * (t - IMPACT_AT));

  return {
    settledAt: IMPACT_AT + 0.6,
    place(t) {
      if (t < IMPACT_AT) return HIDDEN;
      const since = t - IMPACT_AT;
      return {
        x: stage.cx + 7 * Math.sin(70 * since) * Math.exp(-7 * since),
        scale: lerp(0.9, 1, easeBack(seg(t, IMPACT_AT, IMPACT_AT + 0.4))),
      };
    },
    draw(ctx, t) {
      for (const brick of wall) {
        if (t < brick.at) continue;
        if (t < IMPACT_AT) {
          const p = seg(t, brick.at, brick.at + 0.25);
          ctx.globalAlpha = p;
          drawBrick(
            ctx,
            brick.x,
            lerp(brick.y - 30, brick.y, easeOut(p)),
            brick.w,
            bh,
            brick.color,
          );
          continue;
        }
        const k = t - IMPACT_AT;
        const alpha = 1 - seg(k, 1.0, 1.5);
        if (alpha <= 0) continue;
        ctx.globalAlpha = alpha;
        drawBrick(
          ctx,
          brick.x + brick.vx * k,
          brick.y + brick.vy * k + 750 * k * k,
          brick.w,
          bh,
          brick.color,
          brick.spin * k,
        );
      }
      ctx.globalAlpha = 1;
      drawPuffs(ctx, puffs, t);
      // Hoisted away after the swing settles.
      const pivotY =
        pivot.y - easeIn(seg(t, 4.4, 6.0)) * (stage.height + chain);
      const angle = swing(t);
      const ballX = pivot.x + Math.sin(angle) * chain;
      const ballY = pivotY + Math.cos(angle) * chain;
      ctx.strokeStyle = STEEL;
      ctx.lineWidth = 6;
      ctx.beginPath();
      ctx.moveTo(pivot.x, pivotY);
      ctx.lineTo(ballX, ballY - radius);
      ctx.stroke();
      ctx.strokeStyle = STEEL_LIGHT;
      ctx.lineWidth = 3;
      ctx.setLineDash([8, 6]);
      ctx.stroke();
      ctx.setLineDash([]);
      const shade = ctx.createRadialGradient(
        ballX - 12,
        ballY - 12,
        4,
        ballX,
        ballY,
        radius,
      );
      shade.addColorStop(0, STEEL_LIGHT);
      shade.addColorStop(1, '#1E293B');
      ctx.fillStyle = shade;
      circle(ctx, ballX, ballY, radius);
      ctx.fill();
      ctx.strokeStyle = STEEL;
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.arc(ballX, ballY - radius - 4, 6, 0, TAU);
      ctx.stroke();
    },
  };
};

// ── 7. Forklift ────────────────────────────────────────────────────────────
const forklift: Builder = (stage) => {
  const { height } = stage;
  // At rest the card sits on the forks, 12px in front of the mast.
  const stop = stage.cx - 12 - stage.cardWidth / 2;
  const mastX = (t: number) =>
    t < 1.8
      ? lerp(-200, stop, easeOut(seg(t, 0, 1.8)))
      : lerp(stop, -380, easeIn(seg(t, 5.2, 6.8)));
  const forkY = (t: number) => {
    const low = height - 24;
    const high = stage.bottom + 12;
    return t < 4
      ? lerp(low, high, easeInOut(seg(t, 2.0, 3.8)))
      : lerp(high, low, easeInOut(seg(t, 4.0, 5.0)));
  };
  const loadScale = (t: number) =>
    t < 4 ? lerp(0.35, 1, easeInOut(seg(t, 2.0, 3.8))) : 1;

  return {
    settledAt: 4.0,
    place(t) {
      if (t >= 4) return {};
      const scale = loadScale(t);
      return {
        x: mastX(t) + 12 + (stage.cardWidth * scale) / 2,
        y: forkY(t) - 12 - (stage.cardHeight * scale) / 2,
        scale,
      };
    },
    draw(ctx, t) {
      const mx = mastX(t);
      const fy = forkY(t);
      const palletWidth = stage.cardWidth * loadScale(t);
      // Mast.
      ctx.strokeStyle = STEEL;
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.moveTo(mx - 8, height - 20);
      ctx.lineTo(mx - 8, height - 200);
      ctx.moveTo(mx, height - 20);
      ctx.lineTo(mx, height - 200);
      ctx.stroke();
      ctx.strokeStyle = STEEL_LIGHT;
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(mx - 4, height - 60);
      ctx.lineTo(mx - 4, Math.min(height - 200, fy - 40));
      ctx.stroke();
      // Forks and pallet.
      ctx.strokeStyle = STEEL;
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.moveTo(mx, fy - 30);
      ctx.lineTo(mx, fy);
      ctx.lineTo(mx + 14 + palletWidth, fy);
      ctx.stroke();
      ctx.fillStyle = '#A16207';
      ctx.fillRect(mx + 12, fy - 12, palletWidth, 5);
      ctx.fillRect(mx + 12, fy - 4, palletWidth, 3);
      for (const block of [0, 0.5, 1]) {
        ctx.fillRect(mx + 12 + block * (palletWidth - 12), fy - 12, 12, 11);
      }
      // Body, guard, seat and beacon.
      ctx.fillStyle = STEEL;
      roundRect(ctx, mx - 188, height - 82, 26, 48, 4);
      ctx.fill();
      ctx.fillStyle = YELLOW;
      roundRect(ctx, mx - 170, height - 88, 160, 56, 8);
      ctx.fill();
      ctx.strokeStyle = STEEL;
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(mx - 120, height - 88);
      ctx.lineTo(mx - 120, height - 164);
      ctx.lineTo(mx - 20, height - 164);
      ctx.lineTo(mx - 20, height - 88);
      ctx.stroke();
      ctx.fillStyle = TYRE;
      roundRect(ctx, mx - 104, height - 110, 26, 22, 4);
      ctx.fill();
      const flash = Math.sin(t * 14) > 0;
      ctx.fillStyle = flash ? '#FB923C' : '#C2410C';
      if (flash) {
        ctx.shadowColor = '#FB923C';
        ctx.shadowBlur = 18;
      }
      roundRect(ctx, mx - 78, height - 176, 14, 12, 3);
      ctx.fill();
      ctx.shadowBlur = 0;
      drawWheel(ctx, mx - 140, height - 24, 22, mx / 22);
      drawWheel(ctx, mx - 42, height - 22, 18, mx / 18);
    },
  };
};

// ── 8. Blueprint ───────────────────────────────────────────────────────────
const blueprint: Builder = (stage) => {
  const ink = stage.dark ? '96,165,250' : '37,99,235';
  const pad = 6;
  const outline = cardPerimeter(stage, pad);

  return {
    settledAt: 3.2,
    place: (t) => ({
      opacity: seg(t, 2.4, 3.2),
      scale: lerp(0.985, 1, easeOut(seg(t, 2.4, 3.2))),
    }),
    draw(ctx, t) {
      const { width, height, left, right, top, bottom } = stage;
      const wash = seg(t, 0, 0.6) * (1 - seg(t, 5.4, 7.2));
      if (wash <= 0) return;
      ctx.fillStyle = `rgba(${ink},${0.07 * wash})`;
      ctx.fillRect(0, 0, width, height);
      ctx.lineWidth = 1;
      for (let x = 0, i = 0; x < width; x += 24, i += 1) {
        ctx.strokeStyle = `rgba(${ink},${(i % 5 ? 0.12 : 0.26) * wash})`;
        ctx.beginPath();
        ctx.moveTo(x + 0.5, 0);
        ctx.lineTo(x + 0.5, height);
        ctx.stroke();
      }
      for (let y = 0, i = 0; y < height; y += 24, i += 1) {
        ctx.strokeStyle = `rgba(${ink},${(i % 5 ? 0.12 : 0.26) * wash})`;
        ctx.beginPath();
        ctx.moveTo(0, y + 0.5);
        ctx.lineTo(width, y + 0.5);
        ctx.stroke();
      }
      // Construction lines run out from the card's corners.
      const reach = easeOut(seg(t, 0.3, 1.3));
      ctx.strokeStyle = `rgba(${ink},${0.5 * wash})`;
      ctx.setLineDash([6, 6]);
      ctx.beginPath();
      for (const x of [left - pad, right + pad]) {
        ctx.moveTo(x, top - pad);
        ctx.lineTo(x, lerp(top - pad, 0, reach));
        ctx.moveTo(x, bottom + pad);
        ctx.lineTo(x, lerp(bottom + pad, height, reach));
      }
      for (const y of [top - pad, bottom + pad]) {
        ctx.moveTo(left - pad, y);
        ctx.lineTo(lerp(left - pad, 0, reach), y);
        ctx.moveTo(right + pad, y);
        ctx.lineTo(lerp(right + pad, width, reach), y);
      }
      ctx.stroke();
      ctx.setLineDash([]);
      // The outline is drafted clockwise, a pen point at its head.
      const drafted = outline.total * easeInOut(seg(t, 0.8, 2.4));
      ctx.strokeStyle = `rgba(${ink},${wash})`;
      ctx.lineWidth = 2;
      ctx.beginPath();
      for (let d = 0; d <= drafted; d += 4) {
        const point = outline.at(d);
        if (d === 0) ctx.moveTo(point.x, point.y);
        else ctx.lineTo(point.x, point.y);
      }
      ctx.stroke();
      if (drafted > 0 && drafted < outline.total) {
        const head = outline.at(drafted);
        ctx.fillStyle = `rgba(${ink},${wash})`;
        ctx.shadowColor = `rgb(${ink})`;
        ctx.shadowBlur = 12;
        circle(ctx, head.x, head.y, 4);
        ctx.fill();
        ctx.shadowBlur = 0;
      }
      // Dimension lines with arrowheads and the card's size in pixels.
      const dims = seg(t, 1.6, 2.2) * wash;
      if (dims > 0) {
        ctx.strokeStyle = `rgba(${ink},${dims})`;
        ctx.fillStyle = `rgba(${ink},${dims})`;
        ctx.lineWidth = 1.2;
        const arrow = (x: number, y: number, a: number) => {
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(x - Math.cos(a - 0.4) * 8, y - Math.sin(a - 0.4) * 8);
          ctx.lineTo(x - Math.cos(a + 0.4) * 8, y - Math.sin(a + 0.4) * 8);
          ctx.fill();
        };
        const topLine = top - 30;
        ctx.beginPath();
        ctx.moveTo(left, topLine);
        ctx.lineTo(right, topLine);
        ctx.stroke();
        arrow(left, topLine, Math.PI);
        arrow(right, topLine, 0);
        const sideLine = right + 30;
        ctx.beginPath();
        ctx.moveTo(sideLine, top);
        ctx.lineTo(sideLine, bottom);
        ctx.stroke();
        arrow(sideLine, top, -Math.PI / 2);
        arrow(sideLine, bottom, Math.PI / 2);
        ctx.font = '11px ui-monospace, SFMono-Regular, Menlo, monospace';
        ctx.textAlign = 'center';
        ctx.fillText(
          String(Math.round(stage.cardWidth)),
          stage.cx,
          topLine - 6,
        );
        ctx.save();
        ctx.translate(sideLine + 14, stage.cy);
        ctx.rotate(Math.PI / 2);
        ctx.fillText(String(Math.round(stage.cardHeight)), 0, 0);
        ctx.restore();
      }
      // A title block in the corner: ruled boxes and a tick, no words.
      const block = seg(t, 1.8, 2.6) * wash;
      if (block > 0) {
        const w = 160;
        const h = 56;
        const x = width - w - 18;
        const y = height - h - 18;
        ctx.strokeStyle = `rgba(${ink},${block})`;
        ctx.lineWidth = 1.5;
        ctx.strokeRect(x, y, w, h);
        ctx.beginPath();
        ctx.moveTo(x, y + 20);
        ctx.lineTo(x + w, y + 20);
        ctx.moveTo(x + 100, y + 20);
        ctx.lineTo(x + 100, y + h);
        for (const row of [30, 40, 50]) {
          ctx.moveTo(x + 8, y + row);
          ctx.lineTo(x + 88, y + row);
        }
        ctx.moveTo(x + 8, y + 10);
        ctx.lineTo(x + 120, y + 10);
        ctx.stroke();
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.moveTo(x + 118, y + 38);
        ctx.lineTo(x + 126, y + 46);
        ctx.lineTo(x + 142, y + 28);
        ctx.stroke();
      }
    },
  };
};

// ── 9. Welding ─────────────────────────────────────────────────────────────
const WELD_START = 0.4;
const WELD_END = 3.4;

const welding: Builder = (stage, rng) => {
  const seam = cardPerimeter(stage, 4);
  const half = seam.total / 2;
  const torches = [0, half];
  const sparks: Thrown[] = [];
  for (const start of torches) {
    for (let i = 0; i < 230; i += 1) {
      const te =
        WELD_START +
        (i / 230) * (WELD_END - WELD_START) +
        rand(rng, -0.02, 0.02);
      const from = seam.at(start + half * seg(te, WELD_START, WELD_END));
      sparks.push({
        x: from.x,
        y: from.y,
        vx: rand(rng, -280, 280) + from.nx * 120,
        vy: rand(rng, -380, -40) + from.ny * 120,
        g: 1200,
        te,
        life: rand(rng, 0.4, 0.9),
      });
    }
  }

  return {
    settledAt: 3.6,
    place: (t) => ({
      opacity: seg(t, WELD_START, 3.6),
      scale: lerp(0.98, 1, seg(t, WELD_START, 3.6)),
    }),
    draw(ctx, t) {
      const fade = 1 - seg(t, 5.4, 7.0);
      if (fade <= 0) return;
      const done = half * seg(t, WELD_START, WELD_END);
      ctx.lineCap = 'round';
      ctx.lineWidth = 4;
      for (const start of torches) {
        // The bead cools from white-hot through orange to steel.
        for (let d = 0; d < done; d += 5) {
          const age = t - (WELD_START + (d / half) * (WELD_END - WELD_START));
          ctx.strokeStyle =
            age < 0.25
              ? '#FFF7D6'
              : age < 0.7
                ? '#FCD34D'
                : age < 1.4
                  ? ORANGE
                  : STEEL_LIGHT;
          const a = seam.at(start + d);
          const b = seam.at(start + Math.min(done, d + 5));
          ctx.globalAlpha = fade;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
        if (t > WELD_START && t < WELD_END) {
          const head = seam.at(start + done);
          const radius = 22 * (0.8 + 0.4 * Math.abs(Math.sin(t * 53 + start)));
          const glow = ctx.createRadialGradient(
            head.x,
            head.y,
            0,
            head.x,
            head.y,
            radius,
          );
          glow.addColorStop(0, 'rgba(255,255,255,1)');
          glow.addColorStop(0.3, 'rgba(253,224,71,.8)');
          glow.addColorStop(1, 'rgba(249,115,22,0)');
          ctx.globalAlpha = 1;
          ctx.fillStyle = glow;
          circle(ctx, head.x, head.y, radius);
          ctx.fill();
        }
      }
      ctx.lineWidth = 2;
      for (const spark of sparks) {
        const at = thrownAt(spark, t);
        if (!at) continue;
        const vy = spark.vy + spark.g * at.age;
        ctx.globalAlpha = 1 - at.k;
        ctx.strokeStyle =
          at.k < 0.3 ? '#FFF7D6' : at.k < 0.6 ? '#FCD34D' : ORANGE;
        ctx.beginPath();
        ctx.moveTo(at.x, at.y);
        ctx.lineTo(at.x - spark.vx * 0.025, at.y - vy * 0.025);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    },
  };
};

// ── 10. Ribbon cutting ─────────────────────────────────────────────────────
const CUT_AT = 2.35;

interface Confetto extends Thrown {
  color: string;
  w: number;
  h: number;
  spin: number;
  phase: number;
}

const ribbonCutting: Builder = (stage, rng, palette) => {
  const { width, height } = stage;
  const tapeY = Math.min(height - 70, stage.bottom + 56);
  const leftPost = Math.max(54, width * 0.08);
  const rightPost = width - leftPost;
  const anchors = {
    left: { x: leftPost, y: tapeY - 8 },
    right: { x: rightPost, y: tapeY - 8 },
  };
  const cut = { x: stage.cx, y: tapeY + 14 };
  const smallCount = Math.max(2, Math.floor(width / 90));
  const smallCones = Array.from({ length: smallCount }, (_, i) => ({
    x: 45 + i * ((width - 90) / (smallCount - 1)),
    at: 0.3 + i * 0.05,
  }));
  const colors = [...palette, '#FACC15', '#22C55E', '#3B82F6'];
  const confetti: Confetto[] = Array.from({ length: 150 }, () => {
    const angle = rand(rng, -Math.PI, 0);
    const speed = rand(rng, 280, 820);
    return {
      x: cut.x,
      y: cut.y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      g: 900,
      te: CUT_AT + rand(rng, 0, 0.08),
      life: rand(rng, 2.2, 3.4),
      color: pick(rng, colors),
      w: rand(rng, 6, 12),
      h: rand(rng, 8, 16),
      spin: rand(rng, -9, 9),
      phase: rand(rng, 0, TAU),
    };
  });

  const tape = (ctx: CanvasRenderingContext2D, points: Point[]) => {
    const path = () => {
      ctx.beginPath();
      points.forEach((point, i) =>
        i ? ctx.lineTo(point.x, point.y) : ctx.moveTo(point.x, point.y),
      );
    };
    ctx.lineCap = 'butt';
    ctx.lineJoin = 'round';
    path();
    ctx.strokeStyle = '#FACC15';
    ctx.lineWidth = 16;
    ctx.stroke();
    path();
    ctx.strokeStyle = '#111827';
    ctx.setLineDash([9, 9]);
    ctx.stroke();
    ctx.setLineDash([]);
  };

  return {
    settledAt: CUT_AT + 0.5,
    // Held back until the tape is cut.
    place: (t) =>
      t < CUT_AT
        ? HIDDEN
        : {
            opacity: seg(t, CUT_AT, CUT_AT + 0.2),
            scale: lerp(0.85, 1, easeBack(seg(t, CUT_AT, CUT_AT + 0.5))),
          },
    draw(ctx, t) {
      ctx.globalAlpha = 1 - seg(t, 6.0, 7.2);
      if (t < CUT_AT) {
        // Strung out from the left post, with a slight sag.
        const segments = 40;
        const shown = easeOut(seg(t, 0.5, 1.3));
        const points: Point[] = [];
        for (let i = 0; i <= segments * shown; i += 1) {
          const u = i / segments;
          points.push({
            x: lerp(anchors.left.x, anchors.right.x, u),
            y: anchors.left.y + Math.sin(u * Math.PI) * 22,
          });
        }
        if (points.length > 1) tape(ctx, points);
      } else {
        // Each half swings down from its post and settles.
        const since = t - CUT_AT;
        const drop =
          (Math.PI / 2) * (1 - Math.exp(-2.5 * since) * Math.cos(5 * since));
        for (const side of [-1, 1]) {
          const anchor = side < 0 ? anchors.left : anchors.right;
          const length = Math.abs(cut.x - anchor.x);
          const points: Point[] = [anchor];
          for (let i = 1; i <= 30; i += 1) {
            const bend = drop * Math.pow(i / 30, 0.6);
            const previous = points[i - 1]!;
            points.push({
              x: previous.x - side * Math.cos(bend) * (length / 30),
              y: previous.y + Math.sin(bend) * (length / 30),
            });
          }
          tape(ctx, points);
        }
      }
      drawCone(ctx, leftPost, tapeY + 58, 72, easeBack(seg(t, 0.1, 0.5)));
      drawCone(ctx, rightPost, tapeY + 58, 72, easeBack(seg(t, 0.2, 0.6)));
      for (const cone of smallCones) {
        drawCone(
          ctx,
          cone.x,
          height - 6,
          34,
          easeBack(seg(t, cone.at, cone.at + 0.35)),
        );
      }
      // The scissors fly in, snip, and fly off.
      if (t > 1.3 && t < 3.2) {
        const arrive = easeOut(seg(t, 1.3, 2.1));
        const leave = easeIn(seg(t, 2.6, 3.2));
        const open = lerp(0.5, 0.04, easeIn(seg(t, 2.1, CUT_AT)));
        ctx.save();
        ctx.translate(
          lerp(width + 80, cut.x + 22, arrive) + leave * 260,
          lerp(tapeY - 220, cut.y, arrive) - leave * 260,
        );
        ctx.rotate(-0.25);
        for (const side of [-1, 1]) {
          ctx.save();
          ctx.rotate(side * open);
          ctx.fillStyle = '#CBD5E1';
          ctx.beginPath();
          ctx.moveTo(0, 0);
          ctx.lineTo(-70, side * 3);
          ctx.lineTo(0, side * 9);
          ctx.closePath();
          ctx.fill();
          ctx.strokeStyle = ORANGE;
          ctx.lineWidth = 6;
          ctx.beginPath();
          ctx.ellipse(30, side * 10, 16, 10, 0, 0, TAU);
          ctx.stroke();
          ctx.restore();
        }
        ctx.fillStyle = STEEL;
        circle(ctx, 0, 0, 4);
        ctx.fill();
        ctx.restore();
      }
      ctx.globalAlpha = 1;
      for (const piece of confetti) {
        const at = thrownAt(piece, t);
        if (!at) continue;
        const drag = Math.min(1, 1.6 / (1 + at.age * 2));
        const x =
          piece.x +
          piece.vx * at.age * drag +
          Math.sin(at.age * 4 + piece.phase) * 18;
        const y =
          piece.y +
          piece.vy * at.age * drag +
          0.5 * piece.g * at.age * at.age * 0.55;
        ctx.save();
        ctx.globalAlpha = 1 - seg(at.k, 0.7, 1);
        ctx.translate(x, y);
        ctx.rotate(piece.spin * at.age);
        ctx.scale(1, Math.cos(at.age * 8 + piece.phase));
        ctx.fillStyle = piece.color;
        ctx.fillRect(-piece.w / 2, -piece.h / 2, piece.w, piece.h);
        ctx.restore();
      }
      ctx.globalAlpha = 1;
    },
  };
};

const BUILDERS: Record<CelebrationStyle, Builder> = {
  bricks,
  crane,
  'dump-truck': dumpTruck,
  'concrete-pour': concretePour,
  excavator,
  'wrecking-ball': wreckingBall,
  forklift,
  blueprint,
  welding,
  'ribbon-cutting': ribbonCutting,
  ...SECOND_TEN,
};

/**
 * Styles whose props are drawn over the notice rather than under it
 * (PAY-4644): the tunnel borer buries it in dirt and bores down to it, the
 * hammer drives nails through its corners, and the surveyor pegs its corners.
 * Every other style paints beneath the notice, so nothing it throws can cover
 * the text. The canvas never takes pointer events either way, and the notice
 * is inert until it settles.
 */
export const DRAWS_OVER_CARD: ReadonlySet<CelebrationStyle> = new Set([
  'tunnel-borer',
  'hammer-nails',
  'surveyor',
]);

export function buildConstructionScene(
  style: CelebrationStyle,
  stage: StageGeometry,
  rng: Rng,
  palette: readonly string[],
): ConstructionScene {
  return BUILDERS[style](stage, rng, palette);
}

/** The card's pose at `t`, as an offset from rest. At rest once it has settled. */
export function constructionCardPose(
  scene: ConstructionScene,
  stage: StageGeometry,
  t: number,
): CardPose {
  if (t >= Math.min(scene.settledAt, CELEBRATION_DURATION_S)) {
    return RESTING_POSE;
  }
  const placed = scene.place(t);
  return {
    dx: (placed.x ?? stage.cx) - stage.cx,
    dy: (placed.y ?? stage.cy) - stage.cy,
    rotation: placed.rotation ?? 0,
    scale: placed.scale ?? 1,
    opacity: placed.opacity ?? 1,
  };
}
